import { tWithKeyAttr as t } from "../../../i18nHelper";
import popupStyles from "./cancleCalenderPopup.module.css";
import styles from "../editAvailabilityPopup/editAvailabilityPopup.module.css";
import Calendar from "react-calendar";
import { ITreatmentPlan } from "../../../types/types";
import {
  useChangeAppointmentByCalendarMutation,
  useLazyGetAllAppointmentsQuery,
} from "../../../services/api/user.api";
import { TAppointment } from "../../../services/types/user.types";
import { useEffect, useState } from "react";
import { formatDate, getPrintDate } from "../../../utils/utils";
import { useAppSelector } from "../../../services/hooks";
import {
  calendarAppointmentSelector,
  userSelector,
} from "../../../services/selectors/user.selectors";
import Modal from "../modal/Modal";
import ChangeAppointmentConfirmationPopup from "../changeAppointmentConfirmationPopup/changeAppointmentConfirmationPopup";
import OverwriteAppointmentConfirmationPopup from "../changeAppointmentConfirmationPopup/OverwriteAppointmentConfirmationPopup";
import {
  decrement,
  hoursRange,
  increment,
  minutesRange,
} from "../../../utils/constants";
import i18n from "../../../i18n";

interface CancleCalenderPopupProps {
  date: string;
  treatmentPlan: ITreatmentPlan;
  setProgramDates: (programDatesChanged: TAppointment[] | null) => void;
  onClose: () => void;
  onError: () => void;
  onSuccess: (change: boolean, date: string) => void;
}

function CancleCalenderPopup({
  date,
  treatmentPlan,
  setProgramDates,
  onSuccess,
  onError,
  onClose,
}: CancleCalenderPopupProps) {
  const [activeDate, setActiveDate] = useState<null | Date>(null);
  const [activeStartDate, setActiveStartDate] = useState<Date>(new Date());
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [openOverwriteConfirmationModal, setOpenOverwriteConfirmationModal] =
    useState(false);

  const therapist = useAppSelector(userSelector);
  const userAppointments = useAppSelector(calendarAppointmentSelector);
  const [startTime, setStartTime] = useState<string>("07:00");

  const [getUserAppointments] = useLazyGetAllAppointmentsQuery();
  const [changeAppointmentByCalendar] =
    useChangeAppointmentByCalendarMutation();

  useEffect(() => {
    getUserAppointments({
      therapistGuid: therapist.guid,
      month: (activeStartDate ?? new Date()).getMonth() + 1,
      year: (activeStartDate ?? new Date()).getFullYear(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStartDate]);

  const changeAppointment = (deleteType: number) => {
    const replacDate = formatDate(activeDate?.toDateString() ?? "");
    const { usersId, therapistsId, ...restAppointmentsProgram } =
      treatmentPlan.appointmentsProgram;
    activeDate &&
      changeAppointmentByCalendar({
        ...restAppointmentsProgram,
        dateToDelete: formatDate(date),
        replaceDate: replacDate,
        replaceStartTime: startTime,
        deleteType: deleteType,
        updateUsersGuid: therapist.guid,
      }).then((res) => {
        if ("data" in res) {
          const newDate = res.data.find(
            (item) =>
              new Date(item.appointmentDate).toISOString().split("T")[0] ===
              new Date(activeDate).toISOString().split("T")[0]
          );
          if (newDate) {
            setProgramDates(res.data);
            onClose();
            onSuccess(true, getPrintDate(new Date(newDate.appointmentDate)));
          } else {
            onError();
          }
        } else {
          onError();
        }
      });
  };
  const lang = i18n.language;

  return (
    <article style={{ placeItems: "center" }} className={popupStyles.popup}>
      <p className={popupStyles.popup__text}>
        {t("CANCEL_CALENDAR_POPUP_PROMPT")}
      </p>{" "}
      <p className={popupStyles.popup__text}>{date}</p>
      <div className={popupStyles.calendarSection}>
        <Calendar
          activeStartDate={activeStartDate}
          onActiveStartDateChange={({ activeStartDate }) =>
            setActiveStartDate(activeStartDate!)
          }
          minDetail="month"
          calendarType={"gregory"}
          locale={lang}
          onClickDay={setActiveDate}
          value={activeDate}
          prev2Label={null}
          next2Label={null}
          minDate={new Date()}
          tileDisabled={({ date }) => date.getDay() === 6}
          tileContent={({ date }) => {
            if (
              userAppointments?.find(
                (item) =>
                  new Date(item.appointmentDate).toISOString().split("T")[0] ===
                  new Date(date).toISOString().split("T")[0]
              )
            ) {
              return (
                <div className={popupStyles.circleContainer}>
                  <span className={popupStyles.circle} />
                </div>
              );
            } else {
              return null;
            }
          }}
          tileClassName={({ date, view }) =>
            view === "month" && date < new Date(new Date().setHours(0, 0, 0, 0))
              ? styles.pastDate
              : undefined
          }
        />
      </div>
      {activeDate && (
        <>
          <div className={popupStyles.popup__startTimeContainer}>
            <label className={popupStyles.popup__text} htmlFor="startTime">
              {t("CANCEL_CALENDAR_POPUP_LABEL_START_TIME")}
            </label>{" "}
          </div>
          <div className={`${styles.select} `}>
            <button
              className={`${styles.btn} ${styles.incrementBtn}`}
              onClick={(e) => {
                e.stopPropagation();
                setStartTime(increment(hoursRange, startTime, "h"));
              }}
            />

            <button
              className={`${styles.btn} ${styles.incrementBtn}`}
              onClick={(e) => {
                e.stopPropagation();
                setStartTime(increment(minutesRange, startTime, "m"));
              }}
            />
            <span className={styles.selectText}>{startTime.split(":")[0]}</span>
            {/* <p className={styles.timePickerText}>:</p> */}

            <span className={styles.selectText}>
              {startTime.split(":")[1] ?? "00"}
            </span>

            <button
              className={`${styles.btn} ${styles.decrementBtn}`}
              onClick={(e) => {
                e.stopPropagation();
                setStartTime(decrement(hoursRange, startTime, "h"));
              }}
            />
            <button
              className={`${styles.btn} ${styles.decrementBtn}`}
              onClick={(e) => {
                e.stopPropagation();
                setStartTime(decrement(minutesRange, startTime, "m"));
              }}
            />
          </div>
        </>
      )}
      <button
        disabled={!startTime}
        className={`button ${popupStyles.popup__nextBtn} ${
          popupStyles.popup__nextBtn_white
        } ${!startTime ? popupStyles.popup__nextBtn_disabled : "hover"}`}
        onClick={() => {
          if (!activeDate && !startTime) {
            console.error("No active date selected.");
            return;
          }
          const replacDate = formatDate(activeDate?.toDateString() ?? "");

          const existingAppointment = userAppointments.find((item) => {
            const appointmentDate = new Date(item.appointmentDate);
            return (
              formatDate(appointmentDate.toDateString()) === replacDate &&
              item.startTime === startTime
            );
          });

          if (existingAppointment) {
            setOpenOverwriteConfirmationModal(true);
          } else {
            setOpenConfirmationModal(true);
          }
        }}
      >
        {t("CANCEL_CALENDAR_POPUP_SAVE_BUTTON")}
      </button>
      {openConfirmationModal && activeDate && (
        <Modal
          onClose={() => {
            setOpenConfirmationModal(false);
          }}
        >
          <ChangeAppointmentConfirmationPopup
            date1={date}
            date2={activeDate.toLocaleDateString("en-GB")}
            changeAppointment={changeAppointment}
            onClose={() => {
              setOpenConfirmationModal(false);
            }}
          />
        </Modal>
      )}
      {openOverwriteConfirmationModal && activeDate && (
        <Modal
          onClose={() => {
            setOpenOverwriteConfirmationModal(false);
          }}
        >
          <OverwriteAppointmentConfirmationPopup
            onConfirm={() => {
              changeAppointment(2);
              setOpenOverwriteConfirmationModal(false);
            }}
            onCancel={() => {
              setOpenOverwriteConfirmationModal(false);
            }}
            userName={treatmentPlan.user?.fullName || t("USER")}
          />
        </Modal>
      )}
    </article>
  );
}

export default CancleCalenderPopup;
